import { apiClient } from './client';
import { supabase } from '../supabase/client';

// Client interface for team
export interface Team {
  id: string;
  name: string;
  createdAt: string;
  shiftsPerProvider: number;
  dayProvidersRequired: number;
  nightProvidersRequired: number;
  schedulingPeriodDays: number;
  maxConsecutiveShifts: number; 
  minRestHours: number;
  createdBy: string;
}

// Database interface for team
interface DbTeam {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  shifts_per_provider: number;
  day_providers_required: number;
  night_providers_required: number;
  scheduling_period_days: number;
  max_consecutive_shifts: number;
  min_rest_hours: number;
}

// Create team request
export interface CreateTeamRequest {
  name: string;
  shiftsPerProvider?: number;
  dayProvidersRequired?: number;
  nightProvidersRequired?: number;
  schedulingPeriodDays?: number;
  maxConsecutiveShifts?: number;
  minRestHours?: number;
  createdBy?: string;
}

// Update team request interface
export interface UpdateTeamRequest {
  name?: string;
  shiftsPerProvider?: number;
  dayProvidersRequired?: number;
  nightProvidersRequired?: number;
  schedulingPeriodDays?: number;
  maxConsecutiveShifts?: number;
  minRestHours?: number;
}

// Client interface for team member
export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  fullName: string;
  email: string;
  role: 'admin' | 'provider';
  shiftType: 'days_only' | 'nights_only' | 'both';
  isActive: boolean;
  avatarUrl?: string;
}

// Database interface for team member
interface DbTeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: 'admin' | 'provider';
  shift_type: 'days_only' | 'nights_only' | 'both';
  user?: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  };
}

// Get all teams for the current user
export const getUserTeams = async (): Promise<Team[]> => {
  console.log('getUserTeams: Fetching user teams');
  
  const response = await apiClient<null, DbTeam[]>({
    endpoint: 'teams',
    method: 'GET'
  });
  
  if (response.error || !response.data) {
    console.error('getUserTeams: Failed to get teams', response.error);
    return [];
  }

  console.log('getUserTeams: Teams fetched successfully', response.data);

  // Transform from database column names to camelCase
  return response.data.map(team => ({
    id: team.id,
    name: team.name,
    createdAt: team.created_at,
    createdBy: team.created_by,
    shiftsPerProvider: team.shifts_per_provider,
    dayProvidersRequired: team.day_providers_required,
    nightProvidersRequired: team.night_providers_required,
    schedulingPeriodDays: team.scheduling_period_days,
    maxConsecutiveShifts: team.max_consecutive_shifts,
    minRestHours: team.min_rest_hours
  }));
};

// Get team members
export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
  const response = await apiClient<null, DbTeamMember[]>({
    endpoint: 'team_members',
    method: 'GET',
    params: { 
      team_id: teamId,
      select: 'id,team_id,user_id,role,shift_type,user:users(id,email,full_name,avatar_url)'
    }
  });
  
  if (response.error || !response.data) {
    return [];
  }

  // Transform from database column names to camelCase
  return response.data.map(member => ({
    id: member.id,
    teamId: member.team_id,
    userId: member.user_id,
    role: member.role,
    shiftType: member.shift_type,
    isActive: true,
    fullName: member.user?.full_name || 'Unknown User',
    email: member.user?.email || '',
    avatarUrl: member.user?.avatar_url
  }));
};

// Update team member
export const updateTeamMember = async (
  teamId: string,
  userId: string,
  data: Partial<TeamMember>
): Promise<TeamMember | null> => {
  // Transform from camelCase to database column names
  const dbTeamMember: Partial<DbTeamMember> = {
    team_id: teamId,
    user_id: userId,
    role: data.role,
    shift_type: data.shiftType
  };

  const response = await apiClient<Partial<DbTeamMember>, DbTeamMember[]>({
    endpoint: 'team_members',
    method: 'PUT',
    body: dbTeamMember,
    params: { 
      team_id: teamId,
      user_id: userId
    }
  });
  
  if (response.error || !response.data || response.data.length === 0) {
    return null;
  }

  // Transform back to camelCase
  const member = response.data[0];
  return {
    id: member.id,
    teamId: member.team_id,
    userId: member.user_id,
    role: member.role,
    shiftType: member.shift_type,
    isActive: true,
    fullName: member.user?.full_name || 'Unknown User',
    email: member.user?.email || '',
    avatarUrl: member.user?.avatar_url
  };
};

// Create a new team
export const createTeam = async (data: CreateTeamRequest): Promise<Team> => {
  console.log('Creating team with data:', data);
  
  // Transform from camelCase to database column names
  const dbTeam = {
    name: data.name,
    shifts_per_provider: data.shiftsPerProvider || 13,
    day_providers_required: data.dayProvidersRequired || 2,
    night_providers_required: data.nightProvidersRequired || 1,
    scheduling_period_days: data.schedulingPeriodDays || 28,
    max_consecutive_shifts: data.maxConsecutiveShifts || 5,
    min_rest_hours: data.minRestHours || 10,
    created_by: data.createdBy
  };

  const response = await apiClient<typeof dbTeam, DbTeam[]>({
    endpoint: 'teams',
    method: 'POST',
    body: dbTeam
  });
  
  if (response.error || !response.data) {
    console.error('Failed to create team:', response.error);
    throw new Error(response.error?.message || 'Failed to create team');
  }
  
  console.log('Team created successfully:', response.data);

  // The response.data is an array, get the first team
  const createdTeam = Array.isArray(response.data) ? response.data[0] : response.data;
  
  if (!createdTeam || !createdTeam.id) {
    console.error('Invalid team data returned from API:', createdTeam);
    throw new Error('Failed to create team: Invalid response data');
  }

  // Add the creator as an admin team member
  if (data.createdBy) {
    console.log('Adding creator as admin:', data.createdBy, 'to team:', createdTeam.id);
    try {
      const memberResult = await createTeamMember({
        teamId: createdTeam.id,
        userId: data.createdBy,
        role: 'admin',
        shiftType: 'both'
      });
      console.log('Team member creation result:', memberResult);
    } catch (error) {
      console.error('Error creating team member:', error);
    }
  } else {
    console.warn('No createdBy field provided, skipping team member creation');
  }

  // Transform from database column names to camelCase
  return {
    id: createdTeam.id,
    name: createdTeam.name,
    createdAt: createdTeam.created_at,
    createdBy: createdTeam.created_by,
    shiftsPerProvider: createdTeam.shifts_per_provider,
    dayProvidersRequired: createdTeam.day_providers_required,
    nightProvidersRequired: createdTeam.night_providers_required,
    schedulingPeriodDays: createdTeam.scheduling_period_days,
    maxConsecutiveShifts: createdTeam.max_consecutive_shifts,
    minRestHours: createdTeam.min_rest_hours
  };
};

// Create team member
export const createTeamMember = async (data: {
  teamId: string;
  userId: string;
  role: 'admin' | 'provider';
  shiftType: 'days_only' | 'nights_only' | 'both';
}): Promise<TeamMember | null> => {
  console.log('createTeamMember called with:', data);
  
  // Transform to database column names
  const dbTeamMember = {
    team_id: data.teamId,
    user_id: data.userId,
    role: data.role,
    shift_type: data.shiftType
  };

  console.log('Making API request with data:', dbTeamMember);
  
  const response = await apiClient<typeof dbTeamMember, DbTeamMember[]>({
    endpoint: 'team_members',
    method: 'POST',
    body: dbTeamMember
  });
  
  if (response.error || !response.data || response.data.length === 0) {
    console.error('Failed to create team member:', response.error, response.data);
    return null;
  }

  console.log('Team member created successfully:', response.data);

  // Transform back to camelCase
  const member = response.data[0];
  return {
    id: member.id,
    teamId: member.team_id,
    userId: member.user_id,
    role: member.role,
    shiftType: member.shift_type,
    isActive: true,
    fullName: member.user?.full_name || 'Unknown User',
    email: member.user?.email || '',
    avatarUrl: member.user?.avatar_url
  };
};

// Update an existing team
export const updateTeam = async (teamId: string, data: UpdateTeamRequest): Promise<Team> => {
  // Transform from camelCase to database column names
  const dbTeam = {
    name: data.name,
    shifts_per_provider: data.shiftsPerProvider,
    day_providers_required: data.dayProvidersRequired,
    night_providers_required: data.nightProvidersRequired,
    scheduling_period_days: data.schedulingPeriodDays,
    max_consecutive_shifts: data.maxConsecutiveShifts,
    min_rest_hours: data.minRestHours
  };

  const response = await apiClient<typeof dbTeam, DbTeam>({
    endpoint: 'teams',
    method: 'PUT',
    body: dbTeam,
    params: { id: teamId }
  });
  
  if (response.error || !response.data) {
    throw new Error(response.error?.message || 'Failed to update team');
  }

  // Transform from database column names to camelCase
  const team = response.data;
  return {
    id: team.id,
    name: team.name,
    createdAt: team.created_at,
    createdBy: team.created_by,
    shiftsPerProvider: team.shifts_per_provider,
    dayProvidersRequired: team.day_providers_required,
    nightProvidersRequired: team.night_providers_required,
    schedulingPeriodDays: team.scheduling_period_days,
    maxConsecutiveShifts: team.max_consecutive_shifts,
    minRestHours: team.min_rest_hours
  };
};

// Get a single team by ID
export const getTeam = async (teamId: string): Promise<Team | null> => {
  console.log('getTeam: Fetching team by ID', teamId);
  
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  // Direct Supabase query instead of using apiClient for better control
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();
  
  if (error) {
    console.error('getTeam: Failed to get team', error);
    
    // Special handling for "no rows returned" error
    if (error.code === 'PGRST116' || error.message.includes('no rows')) {
      console.warn(`Team with ID ${teamId} not found`);
      return null;
    }
    
    throw new Error(error?.message || 'Failed to get team');
  }

  if (!data) {
    console.warn(`No data returned for team ${teamId}`);
    return null;
  }

  console.log('getTeam: Team fetched successfully', data);

  // Transform from database column names to camelCase
  return {
    id: data.id,
    name: data.name,
    createdAt: data.created_at,
    createdBy: data.created_by,
    shiftsPerProvider: data.shifts_per_provider,
    dayProvidersRequired: data.day_providers_required,
    nightProvidersRequired: data.night_providers_required,
    schedulingPeriodDays: data.scheduling_period_days,
    maxConsecutiveShifts: data.max_consecutive_shifts,
    minRestHours: data.min_rest_hours
  };
};

// Get team members with user info
export const getTeamMembersWithUserInfo = async (teamId: string) => {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  // Make sure we're showing all team members regardless of user role
  try {
    const { data: teamMembers, error } = await supabase
      .from('team_members')
      .select(`
        *,
        user:user_id (
          id,
          full_name,
          email
        )
      `)
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team members:', error);
      throw new Error(`Failed to fetch team members: ${error.message}`);
    }

    if (!teamMembers || teamMembers.length === 0) {
      console.warn(`No team members found for team ${teamId}`);
    } else {
      console.log(`Found ${teamMembers.length} team members for team ${teamId}`);
    }

    return teamMembers || [];
  } catch (error) {
    console.error('Exception in getTeamMembersWithUserInfo:', error);
    throw error;
  }
};

// Update team member by team_id and user_id
export const updateTeamMemberByIds = async (data: {
  team_id: string;
  user_id: string;
  role?: 'admin' | 'provider';
  shift_type?: 'days_only' | 'nights_only' | 'both';
}) => {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  const updateData: {
    role?: 'admin' | 'provider';
    shift_type?: 'days_only' | 'nights_only' | 'both';
  } = {};
  
  if (data.role) {
    updateData.role = data.role;
  }
  
  if (data.shift_type) {
    updateData.shift_type = data.shift_type;
  }
  
  const { data: teamMember, error } = await supabase
    .from('team_members')
    .update(updateData)
    .eq('team_id', data.team_id)
    .eq('user_id', data.user_id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update team member: ${error.message}`);
  }

  return teamMember;
};

// Remove a member from a team
export const removeTeamMember = async (teamId: string, userId: string) => {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('user_id', userId);

  if (error) {
    throw new Error(`Failed to remove team member: ${error.message}`);
  }
}; 